/**
 * Ramps Index Route - Quick Flow Redirect
 *
 * This route implements the quick flow logic that redirects users
 * to the appropriate page based on existing URL state.
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { RampsText } from "../components/ramps/Text";
import { navigateWithRampsState } from "../lib/ramps-navigation";
import { useRampsStore } from "../stores/use-ramps-store";

function RampsIndexPage() {
	const navigate = useNavigate();
	const search = Route.useSearch(); // Get current search params
	const store = useRampsStore();

	// Calculate redirect route once and navigate immediately
	useMemo(() => {
		// Only redirect if we're at the base index route without a step
		if (!search.step) {
			// Redirect based on current step in store or default to calculator
			const currentStep = store.currentStep || "calculator";
			const stepToRoute: Record<string, string> = {
				calculator: "/ramps/calculator",
				phone: "/ramps/phone",
				name: "/ramps/name",
				address: "/ramps/address",
				identification: "/ramps/identification",
				review: "/ramps/review",
				status: "/ramps/status",
			};

			const route = stepToRoute[currentStep] || "/ramps/calculator";

			// Preserve all search parameters when redirecting
			navigate(
				navigateWithRampsState(search, {
					// biome-ignore lint/suspicious/noExplicitAny: Dynamic route mapping requires flexible typing
					to: route as any,
					search: { step: currentStep },
					replace: true, // Use replace to avoid back button issues
				}),
			);
		}

		return null; // useMemo requires a return value
	}, [search.step, store.currentStep, navigate, search]);

	// Show loading state while redirect happens
	return (
		<div
			className="flex min-h-dvh items-center justify-center"
			style={{ backgroundColor: "var(--ramps-background-base)" }}
		>
			<div className="text-center">
				<div
					className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2"
					style={{ borderColor: "var(--ramps-primary)" }}
				/>
				<RampsText variant="body-default" color="primary">
					Loading Ramps...
				</RampsText>
			</div>
		</div>
	);
}

export const Route = createFileRoute("/ramps/")({
	component: RampsIndexPage,
});
